import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReducersTypes } from "@/redux/reducers/Models";
import {
  createUser,
  createVehicle,
  getVehicles,
  setCustomer,
  validatePlate,
} from "@/redux/ducks/customer";
import { PlateData } from "@/redux/sagas/plate/getData";
import { NavigateFunction } from "react-router-dom";
import { PropsSet } from "../pages/register/Models/vehicle";

const pinScheme = z.object({
  plate: z
    .string()
    .regex(/^\D{3}-\d{1}[A-Za-z0-9]{1}\d{2}$/, "Placa inválida")
    .refine((val) => val.length === 8, "Placa inválida"),
  type: z.string().refine((val) => val !== "0", "Tipo obrigatório"),
  brand: z.string().refine((val) => val !== "0", "Marca obrigatória"),
  model: z.string().refine((val) => val !== "0", "Modelo obrigatório"),
});

type State = {
  modalOpen: boolean;
  loading: boolean;
  error: boolean;
  errorPlate: boolean;
  errorCustomer: boolean;
  errorCreateUser: boolean;
  errorCreateVehicle: boolean;
  success: boolean;
  plateCode: string;
};

export default function useVehicleForm({
  navigate,
  resetFields,
  isLogged,
  getVisitorId,
  vehicleInfos,
}: {
  navigate: NavigateFunction;
  resetFields: (fields: string[], set: PropsSet["set"]) => void;
  isLogged?: boolean;
  getVisitorId?: () => string | null;
  vehicleInfos: {
    brandName?: string;
    modelName?: string;
  };
}) {
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);

  const [ui, setUi] = useState<State>({
    modalOpen: false,
    loading: false,
    error: false,
    errorPlate: false,
    errorCustomer: false,
    errorCreateUser: false,
    errorCreateVehicle: false,
    success: false,
    plateCode: "",
  });

  const unifiedSchema = pinScheme;
  type FormData = z.infer<typeof unifiedSchema>;

  const vehicle = customer?.register?.vehicle;

  const defaultValues: FormData = {
    plate: !isLogged ? (vehicle?.plate ?? "") : "",
    type: "0",
    brand: "0",
    model: "0",
  };

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    reset,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(unifiedSchema),
    mode: "onChange",
    defaultValues,
  });

  const plate = watch("plate");

  const toggleModal = useCallback(() => {
    setUi((prev) => ({ ...prev, modalOpen: !prev.modalOpen }));
  }, []);

  const resetErrors = useCallback(() => {
    setUi((prev) => ({
      ...prev,
      error: false,
      errorPlate: false,
      errorCustomer: false,
      errorCreateUser: false,
      errorCreateVehicle: false,
    }));
  }, []);

  const handleCreateVehicle = useCallback(
    (success: boolean) => {
      setUi((prev) => ({ ...prev, loading: false }));

      if (!success) {
        setUi((prev) => ({ ...prev, errorCreateVehicle: true }));
        toggleModal();
        return;
      }

      dispatch(getVehicles());

      setUi((prev) => ({ ...prev, success: true }));
      toggleModal();
      reset();
    },
    [reset, dispatch, toggleModal],
  );

  const handleCreateUser = useCallback(
    (success: boolean, data: { orderId?: string; errorMessage?: string }) => {
      setUi((prev) => ({ ...prev, loading: false }));

      if (!success) {
        if (data.errorMessage === "already") {
          setUi((prev) => ({ ...prev, errorCustomer: true }));
        }
        if (data.errorMessage === "timeout") {
          setUi((prev) => ({ ...prev, errorCreateUser: true }));
        }
        toggleModal();
        return;
      }

      dispatch(
        setCustomer({
          ...customer,
          register: {
            ...customer?.register,
            orderId: data?.orderId!,
          },
        }),
      );

      navigate("/register/access");
      reset();
    },
    [reset, dispatch, navigate, customer, toggleModal],
  );

  const dispatchCreateUser = useCallback(() => {
    dispatch(
      createUser({
        data: vehicleInfos,
        callback: handleCreateUser,
      }),
    );
  }, [dispatch, vehicleInfos, handleCreateUser]);

  const dispatchCreateVehicle = useCallback(
    (data: FormData) => {
      dispatch(
        createVehicle({
          visitorId: getVisitorId?.()!,
          vehicleInfos,
          vehicle: {
            plate,
            type: data.type,
            brand: data.brand,
            model: data.model,
            plateCode: ui.plateCode,
          },
          callback: handleCreateVehicle,
        }),
      );
    },
    [
      dispatch,
      getVisitorId,
      vehicleInfos,
      plate,
      ui.plateCode,
      handleCreateVehicle,
    ],
  );

  const retryCreateUser = useCallback(() => {
    if (ui.loading) return;
    resetErrors();
    setUi((prev) => ({ ...prev, loading: true, modalOpen: false }));
    dispatchCreateUser();
  }, [ui.loading, resetErrors, dispatchCreateUser]);

  const onSubmit = useCallback(
    (data: FormData) => {
      if (ui.loading) return;
      resetErrors();
      setUi((prev) => ({ ...prev, loading: true }));
      if (isLogged) {
        dispatchCreateVehicle(data);
        return;
      }
      dispatchCreateUser();
    },
    [
      ui.loading,
      isLogged,
      resetErrors,
      dispatchCreateUser,
      dispatchCreateVehicle,
    ],
  );

  const handleValidatePlate = useCallback(
    (
      success: boolean,
      data?:
        | {
            hash?: string;
            plateData?: PlateData;
          }
        | string,
    ) => {
      setUi((prev) => ({ ...prev, loading: false }));

      resetFields(["type", "brand", "model"], setValue as PropsSet["set"]);

      if (!success) {
        if (data === "already") {
          setUi((prev) => ({ ...prev, errorPlate: true }));
        }
        if (data === "timeout") {
          setUi((prev) => ({ ...prev, error: true }));
        }
        toggleModal();
        return;
      }

      if (isLogged) {
        setUi((prev) => ({ ...prev, plateCode: data as string }));
        return;
      }

      dispatch(
        setCustomer({
          ...customer,
          register: {
            ...customer?.register,
            vehicle: {
              ...customer?.register?.vehicle,
              plate,
              plateCode: data as string,
            },
          },
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customer, getValues, dispatch, setValue, resetFields, toggleModal],
  );

  const handlePlate = useCallback(() => {
    if (ui.loading) return;

    if (plate && plate.length === 8) {
      resetErrors();
      setUi((prev) => ({ ...prev, loading: true }));
      if (!isLogged) {
        dispatch(
          setCustomer({
            ...customer,
            register: {
              ...customer?.register,
              vehicle: {
                ...customer?.register?.vehicle,
                plate,
              },
            },
          }),
        );
        dispatch(
          validatePlate({
            plate: plate,
            document: customer?.register?.document!,
            pin: customer?.register?.pin!,
            hashCode: customer?.register?.hashCode!,
            callback: handleValidatePlate,
          }),
        );
        return;
      }
      dispatch(
        validatePlate({
          plate: plate,
          document: customer?.username!,
          callback: handleValidatePlate,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plate, dispatch, customer, ui.loading, handleValidatePlate]);

  useEffect(() => {
    handlePlate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plate]);

  return {
    toggleModal,
    isVisibleModal: ui.modalOpen,
    isLoading: ui.loading,
    isDisabled:
      !isValid || ui.loading || !!(isLogged && ui.plateCode?.length === 0),
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    handlePlate,
    retryCreateUser,
    error: ui.error,
    errorPlate: ui.errorPlate,
    errorCustomer: ui.errorCustomer,
    errorCreateUser: ui.errorCreateUser,
    control,
    setValue,
    getValues,
    success: ui.success,
    errorCreateVehicle: ui.errorCreateVehicle,
  };
}
